// ui-kit: hand-built
import {
    type ComponentPropsWithRef,
    type ReactNode,
    type Ref,
    useEffect,
    useLayoutEffect,
    useRef,
} from 'react';

import { classNames } from '../../internal/classNames';
import { mergeRefs } from '../../internal/mergeRefs';

export type MenuProps = ComponentPropsWithRef<'div'> & {
    children?: ReactNode;
    /** Focus the first enabled item on mount (menus opened from a trigger). */
    autoFocusItem?: boolean;
    ref?: Ref<HTMLDivElement>;
    'data-test-component'?: string;
};

/** Menu surface from Figma Menu (refactor). Rows are `MenuItem` children. */
const Menu = ({
    children,
    className = '',
    autoFocusItem = false,
    onFocus,
    onKeyDown,
    ref,
    role = 'listbox',
    'data-test-component': testComponent = 'ui-kit/Menu',
    ...rest
}: MenuProps) => {
    const menuRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        const items = getFocusableItems(menuRef.current);
        if (items.length > 0 && !items.some((item) => item.tabIndex === 0)) {
            items[0].tabIndex = 0;
        }
    });

    useEffect(() => {
        if (autoFocusItem) {
            getFocusableItems(menuRef.current)[0]?.focus({ preventScroll: true });
        }
    }, [autoFocusItem]);

    return (
        <div
            {...rest}
            ref={mergeRefs(menuRef, ref)}
            role={role}
            className={classNames({
                'ui-menu': true,
                'ui-menu-list': true,
                [className]: Boolean(className),
            })}
            data-test-component={testComponent}
            onFocus={(e) => {
                onFocus?.(e);
                const items = getFocusableItems(e.currentTarget);
                if (!items.includes(e.target as HTMLElement)) {
                    return;
                }
                items.forEach((item) => {
                    item.tabIndex = item === e.target ? 0 : -1;
                });
            }}
            onKeyDown={(e) => {
                onKeyDown?.(e);
                if (e.defaultPrevented || (e.key !== 'ArrowDown' && e.key !== 'ArrowUp')) {
                    return;
                }
                const items = getFocusableItems(e.currentTarget);
                const current = items.indexOf(document.activeElement as HTMLElement);
                if (current === -1) {
                    return;
                }
                e.preventDefault();
                const step = e.key === 'ArrowDown' ? 1 : -1;
                items[(current + step + items.length) % items.length].focus();
            }}>
            {children}
        </div>
    );
};

Menu.displayName = 'Menu';

export default Menu;

function getFocusableItems(menu: HTMLElement | null): HTMLElement[] {
    return Array.from(menu?.querySelectorAll<HTMLElement>('.ui-menu-item[tabindex]') ?? []);
}
